import { pick } from 'lodash-es'

export const SEARCH_API_PAGESIZE_DEFAULT = 20
export const SEARCH_API_PAGESIZE_MAX = 999
export const SEARCH_API_PAGESIZE_NONE = -1

export const SEARCH_API_ALLOWED_PARAMS = [
  'language',
  'currency',
  'season',
  'page',
  'pagesize',
  'sorting',
  'brand',
  'salesoffice',
  'q',
  'checkin',
  'checkout',
  'duration',
  'country',
  'region',
  'place',
  'code',
  'geo',
  'n',
  'w',
  's',
  'e',
  'zoom',
  'lat',
  'lon',
  'radius',
  'pax',
  'pets',
  'house',
  'apartment',
  'villa',
  'chalet',
  'farmhouse',
  'detached',
  'stars',
  'rating',
  'reviews',
  'bedrooms',
  'bathrooms',
  'sea',
  'lake',
  'ski',
  'center',
  'public-transport',
  'wlan',
  'aircondition',
  'parking',
  'garage',
  'balcony-or-terrace',
  'dishwasher',
  'washingmachine',
  'tv',
  'sea_or_lake_view',
  'bbq',
  'boat',
  'cots',
  'hottub',
  'fireplace',
  'sauna',
  'wheelchair',
  'charging_station',
  'pool',
  'pool_private',
  'pool_indoor',
  'pool_children',
  'min-price',
  'maxPrice',
  'discount',
  'special_offer',
  'last_minute',
  'early_booker',
  'discount-20',
  'cheapcheap',
  'familyfriendly',
  'holiday_resort',
  'residence',
  'citytrips',
  'casa',
  'swiss_peak',
  'workation',
  'sustainable',
  'skiing',
  'hiking',
  'golfing',
  'cycling',
  'wellness',
  'tennis',
  'surfing',
  'sailing',
  'mountainbiking',
  'riding',
  'crosscountryskiing',
  'fishing',
  'fishing_certified',
  'studio',
  'lso',
  'bo',
  'rooms',
  'facet',
  'proptype',
  'accomtype',
  'minpets',

  // Freweek
  'freeweek',
  'category',
] as const satisfies ReadonlyArray<keyof SearchApiParams>

/**
 * Maps param values and leaves only those params that the new Search API can accept:
 * - converts date fields to server date format
 * - removes redundant params because otherwise the API will return "Bad Request"
 *
 * @param params Raw params.
 * @returns Only params that the new Search API can accept.
 */
export function mapSearchApiParams<T extends SearchApiParams>(
  params: T,
): SearchApiParams {
  const [checkin, checkout] = [params.checkin, params.checkout].map((date) =>
    date ? toServerDate(date) : undefined,
  )

  return pick({ ...params, checkin, checkout }, SEARCH_API_ALLOWED_PARAMS)
}

export function getCumulativeFacet(
  facet: Record<string, number | undefined>,
  options?: { reverse?: boolean },
): SearchApiCountFacet {
  const entries = Object.entries(facet)

  if (options?.reverse) entries.reverse()

  let total = 0
  return Object.fromEntries(
    entries.map(([value, count]) => [value, (total += count ?? 0)]),
  )
}

export function mapSearchResult(
  context: { cancelConditions?: CancelCondition[] },
  result: SearchApiResult,
  query: SearchApiParams,
  options = { facets: true },
): SearchResult {
  const { page = 1, pagesize = SEARCH_API_PAGESIZE_DEFAULT } = query
  const positionStart = (page - 1) * pagesize

  return {
    ...result,
    // TODO: Remove as soon as the backend starts returning already normalized facets.
    // Since technically only the backend knows about the facet aggregation rules.
    facets:
      options.facets && result.facets
        ? normalizeFacets(result.facets)
        : result.facets,
    docs: result.docs.map<SearchResultDocument>((doc, index) => ({
      ...doc,
      cancelConditions: context.cancelConditions ?? [],
      position: positionStart + index + 1,
    })),
  }
}

function cumulative(reverse = false) {
  return (facet: SearchApiCountFacet) => getCumulativeFacet(facet, { reverse })
}

const FACETS_NORMALIZATION_MAP: {
  [K in keyof Required<SearchApiFacets>]?: (
    facet: Required<SearchApiFacets>[K],
  ) => SearchApiFacets[K]
} = {
  sea: cumulative(),
  lake: cumulative(),
  ski: cumulative(),
  center: cumulative(),
  'public-transport': cumulative(),
}

export function normalizeFacets(facets: SearchApiFacets): SearchApiFacets {
  return Object.fromEntries(
    Object.entries(facets).map(([key, facet]) => [
      key,
      key in FACETS_NORMALIZATION_MAP
        ? FACETS_NORMALIZATION_MAP[key as keyof SearchApiFacets]!(facet)
        : facet,
    ]),
  ) as SearchApiFacets
}
